import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Crown, Download, Search } from 'lucide-react';
import { AdminLayout } from '../components/AdminLayout';
import { api } from '../services/api';

type LoyaltyKpis = {
  companies_total: number;
  companies_tier_enabled: number;
  companies_with_configured_tiers: number;
  total_tiers: number;
  customers_total: number;
  customers_with_tier: number;
  customer_tier_assignment_rate: number;
};

type TierDistribution = {
  id: number;
  name: string;
  company_id: number;
  assigned_customers: number;
};

type CompanyRow = {
  id: number;
  name: string;
  email?: string;
  status?: string;
  plan_name?: string;
  tier_program_enabled: boolean;
  tiers_count: number;
  customers_count: number;
  customers_with_tier_count: number;
  assignment_rate: number;
};

type PlanOption = {
  id: number;
  name: string;
};

type LoyaltyFilterPreset = {
  id: string;
  name: string;
  search: string;
  plan_id: string;
  status: string;
  tier_program_enabled: string;
  adoption_range: string;
  sort_by: string;
  sort_dir: 'asc' | 'desc';
  per_page: number;
};

type CompanyDetails = {
  company: {
    id: number;
    name: string;
    plan_name?: string;
    tier_program_enabled: boolean;
    customers_count: number;
    customers_with_tier_count: number;
  };
  settings?: {
    evaluation_period_days: number;
    allow_downgrade: boolean;
    downgrade_grace_days: number;
  } | null;
  tiers: Array<{
    id: number;
    name: string;
    rank: number;
    points_multiplier: string;
    min_points_earned: number;
    min_purchase_count: number;
    min_total_spent: string;
    benefits: Array<{
      id: number;
      name: string;
      benefit_type: string;
      description?: string | null;
    }>;
  }>;
};

type TrendPoint = {
  date: string;
  new_assigned: number;
  assigned_customers: number;
  total_customers: number;
  adoption_rate: number;
};

const Wrap = styled.div`
  display: grid;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.6rem;
  color: #0f172a;
`;

const Sub = styled.p`
  margin: 0;
  color: #64748b;
`;

const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;
`;

const KpiCard = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px;
`;

const KpiValue = styled.div`
  font-size: 1.35rem;
  font-weight: 800;
  color: #0f172a;
`;

const KpiLabel = styled.div`
  font-size: 0.82rem;
  color: #64748b;
`;

const Section = styled.div`
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
`;

const SectionTitle = styled.h2`
  margin: 0 0 10px;
  font-size: 1rem;
  color: #0f172a;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;

  th, td {
    text-align: left;
    padding: 9px 8px;
    border-bottom: 1px solid #f1f5f9;
    font-size: 0.88rem;
    color: #0f172a;
  }

  th {
    font-size: 0.78rem;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  padding: 0 8px;
  width: min(340px, 100%);
  margin-bottom: 10px;
`;

const SearchInput = styled.input`
  border: none;
  outline: none;
  width: 100%;
  padding: 9px 0;
`;

const FiltersGrid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr repeat(4, minmax(140px, 1fr));
  gap: 8px;
  margin-bottom: 10px;

  @media (max-width: 1200px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const Select = styled.select`
  border: 1px solid #dbe2ea;
  border-radius: 8px;
  padding: 9px 10px;
  background: #fff;
`;

const Ghost = styled.button`
  border: 1px solid #dbe2ea;
  background: #fff;
  border-radius: 8px;
  padding: 7px 10px;
  font-weight: 600;
  cursor: pointer;
`;

const Badge = styled.span<{ $on: boolean }>`
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${props => (props.$on ? '#065f46' : '#7f1d1d')};
  background: ${props => (props.$on ? '#d1fae5' : '#fee2e2')};
`;

const SmallActions = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
`;

const Pagination = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  flex-wrap: wrap;
`;

const PageInfo = styled.span`
  color: #64748b;
  font-size: 0.84rem;
`;

const SortableTh = styled.th`
  cursor: pointer;
  user-select: none;
`;

const TrendHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const TrendGrid = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const TrendCanvas = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  background: #f8fafc;
  min-height: 190px;
  display: flex;
  align-items: end;
  gap: 2px;
`;

const TrendBar = styled.div<{ $height: number; $color: string }>`
  flex: 1;
  min-width: 3px;
  height: ${props => props.$height}%;
  background: ${props => props.$color};
  border-radius: 2px 2px 0 0;
`;

const TrendSummary = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 10px;
  background: #fff;
`;

export const AdminLoyaltyPage: React.FC = () => {
  const [kpis, setKpis] = useState<LoyaltyKpis | null>(null);
  const [distribution, setDistribution] = useState<TierDistribution[]>([]);
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<CompanyDetails | null>(null);
  const [trendDays, setTrendDays] = useState(30);
  const [trendSeries, setTrendSeries] = useState<TrendPoint[]>([]);
  const [trendSummary, setTrendSummary] = useState<{ start_rate: number; end_rate: number; delta_rate: number } | null>(null);
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [presets, setPresets] = useState<LoyaltyFilterPreset[]>([]);
  const [selectedPresetId, setSelectedPresetId] = useState('');
  const [filters, setFilters] = useState({
    plan_id: '',
    status: '',
    tier_program_enabled: '',
    adoption_range: '',
  });
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(30);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);

  const storageKey = 'admin_loyalty_filter_presets_v1';

  const buildQueryParams = (
    term = search,
    overrides?: Partial<{
      page: number;
      per_page: number;
      sort_by: string;
      sort_dir: 'asc' | 'desc';
    }>
  ) => ({
    search: term || undefined,
    page: overrides?.page ?? page,
    per_page: overrides?.per_page ?? perPage,
    sort_by: overrides?.sort_by ?? sortBy,
    sort_dir: overrides?.sort_dir ?? sortDir,
    plan_id: filters.plan_id || undefined,
    status: filters.status || undefined,
    tier_program_enabled: filters.tier_program_enabled || undefined,
    adoption_range: filters.adoption_range || undefined,
  });

  const loadOverview = async () => {
    const response = await api.get('/admin/loyalty/overview', {
      params: buildQueryParams(),
    });
    const data = response.data.data || {};
    setKpis(data.kpis || null);
    setDistribution(data.tier_distribution || []);
  };

  const loadTrends = async () => {
    const response = await api.get('/admin/loyalty/trends', {
      params: {
        ...buildQueryParams(),
        days: trendDays,
      },
    });
    const data = response.data.data || {};
    setTrendSeries(data.series || []);
    setTrendSummary(data.summary || null);
  };

  const loadCompanies = async (
    term = '',
    overrides?: Partial<{
      page: number;
      per_page: number;
      sort_by: string;
      sort_dir: 'asc' | 'desc';
    }>
  ) => {
    const response = await api.get('/admin/loyalty/companies', {
      params: buildQueryParams(term, overrides),
    });
    const payload = response.data.data || {};
    setCompanies(payload.data || []);
    setPlans(response.data.meta?.filters?.plans || []);
    setPage(Number(payload.current_page || 1));
    setLastPage(Number(payload.last_page || 1));
    setTotal(Number(payload.total || 0));
  };

  const openCompany = async (companyId: number) => {
    const response = await api.get(`/admin/loyalty/companies/${companyId}`);
    setSelected(response.data.data || null);
  };

  useEffect(() => {
    loadOverview();
    loadCompanies();
    loadTrends();
  }, []);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      const parsed = raw ? JSON.parse(raw) : [];
      setPresets(Array.isArray(parsed) ? parsed : []);
    } catch {
      setPresets([]);
    }
  }, []);

  const applyFilters = async () => {
    setPage(1);
    await loadOverview();
    await loadTrends();
    await loadCompanies(search, { page: 1 });
  };

  const clearFilters = async () => {
    setFilters({
      plan_id: '',
      status: '',
      tier_program_enabled: '',
      adoption_range: '',
    });
    setSearch('');
    setSortBy('id');
    setSortDir('desc');
    setPerPage(30);
    setPage(1);
    setSelectedPresetId('');
    setSelected(null);
    const params = {
      page: 1,
      per_page: 30,
      sort_by: 'id',
      sort_dir: 'desc',
    };
    const [overviewResponse, trendsResponse, companiesResponse] = await Promise.all([
      api.get('/admin/loyalty/overview', { params }),
      api.get('/admin/loyalty/trends', { params: { ...params, days: trendDays } }),
      api.get('/admin/loyalty/companies', { params }),
    ]);
    const overviewData = overviewResponse.data.data || {};
    setKpis(overviewData.kpis || null);
    setDistribution(overviewData.tier_distribution || []);
    const trendData = trendsResponse.data?.data || {};
    setTrendSeries(trendData.series || []);
    setTrendSummary(trendData.summary || null);
    const companiesPayload = companiesResponse.data?.data || {};
    setCompanies(companiesPayload.data || []);
    setPlans(companiesResponse.data?.meta?.filters?.plans || []);
    setPage(Number(companiesPayload.current_page || 1));
    setLastPage(Number(companiesPayload.last_page || 1));
    setTotal(Number(companiesPayload.total || 0));
  };

  const exportCsv = async () => {
    const response = await api.get('/admin/loyalty/companies-export', {
      params: buildQueryParams(),
      responseType: 'blob',
    });
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `fidelizacao-empresas-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const toggleSort = async (column: string) => {
    const nextDir: 'asc' | 'desc' = sortBy === column && sortDir === 'asc' ? 'desc' : 'asc';
    setSortBy(column);
    setSortDir(nextDir);
    setPage(1);
    await loadOverview();
    await loadTrends();
    await loadCompanies(search, { page: 1, sort_by: column, sort_dir: nextDir });
  };

  const savePreset = () => {
    const name = window.prompt('Nome do preset de filtro:');
    if (!name || !name.trim()) return;
    const nextPreset: LoyaltyFilterPreset = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      name: name.trim(),
      search,
      plan_id: filters.plan_id,
      status: filters.status,
      tier_program_enabled: filters.tier_program_enabled,
      adoption_range: filters.adoption_range,
      sort_by: sortBy,
      sort_dir: sortDir,
      per_page: perPage,
    };
    const next = [nextPreset, ...presets].slice(0, 20);
    setPresets(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
    setSelectedPresetId(nextPreset.id);
  };

  const applyPreset = async (presetId: string) => {
    setSelectedPresetId(presetId);
    const preset = presets.find(item => item.id === presetId);
    if (!preset) return;
    setSearch(preset.search);
    setFilters({
      plan_id: preset.plan_id,
      status: preset.status,
      tier_program_enabled: preset.tier_program_enabled,
      adoption_range: preset.adoption_range,
    });
    setSortBy(preset.sort_by);
    setSortDir(preset.sort_dir);
    setPerPage(preset.per_page);
    setPage(1);
    await loadOverview();
    await loadTrends();
    await loadCompanies(preset.search, {
      page: 1,
      per_page: preset.per_page,
      sort_by: preset.sort_by,
      sort_dir: preset.sort_dir,
    });
  };

  const deletePreset = () => {
    if (!selectedPresetId) return;
    const next = presets.filter(item => item.id !== selectedPresetId);
    setPresets(next);
    localStorage.setItem(storageKey, JSON.stringify(next));
    setSelectedPresetId('');
  };

  const goToPage = async (nextPage: number) => {
    const target = Math.min(Math.max(1, nextPage), lastPage || 1);
    setPage(target);
    await loadCompanies(search, { page: target });
  };

  return (
    <AdminLayout>
      <Wrap>
        <Header>
          <div>
            <Title>Fidelização Global</Title>
            <Sub>Monitoramento de adoção do programa de níveis por empresa.</Sub>
          </div>
          <Ghost onClick={() => { loadOverview(); loadCompanies(search); }}>
            Atualizar
          </Ghost>
        </Header>

        {kpis && (
          <KpiGrid>
            <KpiCard><KpiValue>{kpis.companies_total}</KpiValue><KpiLabel>Empresas</KpiLabel></KpiCard>
            <KpiCard><KpiValue>{kpis.companies_tier_enabled}</KpiValue><KpiLabel>Com tier habilitado no plano</KpiLabel></KpiCard>
            <KpiCard><KpiValue>{kpis.companies_with_configured_tiers}</KpiValue><KpiLabel>Com tiers configurados</KpiLabel></KpiCard>
            <KpiCard><KpiValue>{kpis.total_tiers}</KpiValue><KpiLabel>Total de tiers</KpiLabel></KpiCard>
            <KpiCard><KpiValue>{kpis.customers_with_tier}/{kpis.customers_total}</KpiValue><KpiLabel>Clientes com tier</KpiLabel></KpiCard>
            <KpiCard><KpiValue>{kpis.customer_tier_assignment_rate}%</KpiValue><KpiLabel>Taxa de atribuição</KpiLabel></KpiCard>
          </KpiGrid>
        )}

        <Section>
          <TrendHeader>
            <SectionTitle>Tendência de adoção (últimos dias)</SectionTitle>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <Select
                value={trendDays}
                onChange={async e => {
                  const value = Number(e.target.value);
                  setTrendDays(value);
                  const response = await api.get('/admin/loyalty/trends', {
                    params: {
                      ...buildQueryParams(),
                      days: value,
                    },
                  });
                  const data = response.data.data || {};
                  setTrendSeries(data.series || []);
                  setTrendSummary(data.summary || null);
                }}
              >
                <option value={30}>30 dias</option>
                <option value={60}>60 dias</option>
                <option value={90}>90 dias</option>
              </Select>
              <Ghost onClick={loadTrends}>Atualizar tendência</Ghost>
            </div>
          </TrendHeader>
          <TrendGrid>
            <TrendCanvas title="Taxa diária de adoção (%)">
              {trendSeries.map(point => (
                <TrendBar
                  key={`rate-${point.date}`}
                  $height={Math.max(2, Math.min(100, point.adoption_rate))}
                  $color="linear-gradient(180deg, #0ea5e9, #0284c7)"
                />
              ))}
            </TrendCanvas>
            <TrendSummary>
              <div><strong>Início:</strong> {trendSummary?.start_rate ?? 0}%</div>
              <div><strong>Fim:</strong> {trendSummary?.end_rate ?? 0}%</div>
              <div><strong>Variação:</strong> {trendSummary?.delta_rate ?? 0} p.p.</div>
              <div style={{ marginTop: 8, fontSize: '0.82rem', color: '#64748b' }}>
                Cada barra representa a taxa de clientes com tier por dia.
              </div>
            </TrendSummary>
          </TrendGrid>
        </Section>

        <Section>
          <SectionTitle>Top tiers por volume</SectionTitle>
          <Table>
            <thead>
              <tr>
                <th>Tier</th>
                <th>Empresa</th>
                <th>Clientes atribuídos</th>
              </tr>
            </thead>
            <tbody>
              {distribution.map(item => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>#{item.company_id}</td>
                  <td>{item.assigned_customers}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>

        <Section>
          <SectionTitle>Empresas e programa de níveis</SectionTitle>
          <FiltersGrid>
            <SearchBox>
              <Search size={15} color="#64748b" />
              <SearchInput
                placeholder="Buscar por nome/email/cnpj..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    applyFilters();
                  }
                }}
              />
            </SearchBox>
            <Select value={filters.plan_id} onChange={e => setFilters(prev => ({ ...prev, plan_id: e.target.value }))}>
              <option value="">Todos os planos</option>
              {plans.map(plan => (
                <option key={plan.id} value={plan.id}>{plan.name}</option>
              ))}
            </Select>
            <Select value={filters.status} onChange={e => setFilters(prev => ({ ...prev, status: e.target.value }))}>
              <option value="">Todos os status</option>
              <option value="active">Ativa</option>
              <option value="pending">Pendente</option>
              <option value="suspended">Suspensa</option>
              <option value="inactive">Inativa</option>
            </Select>
            <Select value={filters.tier_program_enabled} onChange={e => setFilters(prev => ({ ...prev, tier_program_enabled: e.target.value }))}>
              <option value="">Tier no plano (todos)</option>
              <option value="1">Com tier no plano</option>
              <option value="0">Sem tier no plano</option>
            </Select>
            <Select value={filters.adoption_range} onChange={e => setFilters(prev => ({ ...prev, adoption_range: e.target.value }))}>
              <option value="">Faixa de adoção (todas)</option>
              <option value="0_24">0% a 24%</option>
              <option value="25_49">25% a 49%</option>
              <option value="50_74">50% a 74%</option>
              <option value="75_99">75% a 99%</option>
              <option value="100">100%</option>
            </Select>
          </FiltersGrid>
          <SmallActions>
            <Select
              value={selectedPresetId}
              onChange={e => applyPreset(e.target.value)}
              style={{ minWidth: 220 }}
            >
              <option value="">Presets salvos...</option>
              {presets.map(preset => (
                <option key={preset.id} value={preset.id}>{preset.name}</option>
              ))}
            </Select>
            <Ghost onClick={savePreset}>Salvar preset</Ghost>
            <Ghost onClick={deletePreset} disabled={!selectedPresetId}>Remover preset</Ghost>
            <Ghost onClick={applyFilters}>Aplicar filtros</Ghost>
            <Ghost onClick={clearFilters}>Limpar</Ghost>
            <Ghost onClick={exportCsv}>
              <Download size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
              Exportar CSV
            </Ghost>
          </SmallActions>
          <Table>
            <thead>
              <tr>
                <SortableTh onClick={() => toggleSort('name')}>Empresa {sortBy === 'name' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortableTh>
                <th>Plano</th>
                <SortableTh onClick={() => toggleSort('status')}>Status {sortBy === 'status' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortableTh>
                <th>Tier no plano</th>
                <SortableTh onClick={() => toggleSort('tiers_count')}>Tiers {sortBy === 'tiers_count' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortableTh>
                <SortableTh onClick={() => toggleSort('customers_with_tier_count')}>Clientes c/ tier {sortBy === 'customers_with_tier_count' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortableTh>
                <SortableTh onClick={() => toggleSort('assignment_rate')}>Taxa {sortBy === 'assignment_rate' ? (sortDir === 'asc' ? '↑' : '↓') : ''}</SortableTh>
                <th>Ação</th>
              </tr>
            </thead>
            <tbody>
              {companies.map(company => (
                <tr key={company.id}>
                  <td>{company.name}</td>
                  <td>{company.plan_name || '-'}</td>
                  <td>{company.status || '-'}</td>
                  <td><Badge $on={company.tier_program_enabled}>{company.tier_program_enabled ? 'Sim' : 'Não'}</Badge></td>
                  <td>{company.tiers_count}</td>
                  <td>{company.customers_with_tier_count}/{company.customers_count}</td>
                  <td>{company.assignment_rate}%</td>
                  <td><Ghost onClick={() => openCompany(company.id)}>Detalhar</Ghost></td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Pagination>
            <PageInfo>Pagina {page} de {lastPage} | Total: {total}</PageInfo>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Select value={perPage} onChange={async e => {
                const nextPerPage = Number(e.target.value);
                setPerPage(nextPerPage);
                setPage(1);
                await loadCompanies(search, { page: 1, per_page: nextPerPage });
              }}>
                <option value={10}>10 / pagina</option>
                <option value={20}>20 / pagina</option>
                <option value={30}>30 / pagina</option>
                <option value={50}>50 / pagina</option>
                <option value={100}>100 / pagina</option>
              </Select>
              <Ghost onClick={() => goToPage(page - 1)} disabled={page <= 1}>Anterior</Ghost>
              <Ghost onClick={() => goToPage(page + 1)} disabled={page >= lastPage}>Próxima</Ghost>
            </div>
          </Pagination>
        </Section>

        {selected && (
          <Section>
            <SectionTitle>
              <Crown size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              {selected.company.name} - Configuração de tiers
            </SectionTitle>
            <Sub>
              Plano: {selected.company.plan_name || '-'} | Clientes com tier: {selected.company.customers_with_tier_count}/{selected.company.customers_count}
            </Sub>
            <Sub>
              Janela: {selected.settings?.evaluation_period_days ?? '-'} dias | Downgrade: {selected.settings?.allow_downgrade ? 'Sim' : 'Não'} | Grace: {selected.settings?.downgrade_grace_days ?? '-'} dias
            </Sub>
            <Table>
              <thead>
                <tr>
                  <th>Tier</th>
                  <th>Rank</th>
                  <th>Min pontos</th>
                  <th>Min compras</th>
                  <th>Min gasto</th>
                  <th>Multiplicador</th>
                  <th>Benefícios</th>
                </tr>
              </thead>
              <tbody>
                {selected.tiers.map(tier => (
                  <tr key={tier.id}>
                    <td>{tier.name}</td>
                    <td>{tier.rank}</td>
                    <td>{tier.min_points_earned}</td>
                    <td>{tier.min_purchase_count}</td>
                    <td>{tier.min_total_spent}</td>
                    <td>x{tier.points_multiplier}</td>
                    <td>{tier.benefits.length}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Section>
        )}
      </Wrap>
    </AdminLayout>
  );
};
